<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[
        sizeClass,
        isActive ? 'device-active--binary-sensor' : '',
        hasAlarm ? 'alarm-active' : '',
        isSuperviseDetected ? 'supervise-detected' : '',
      ]"
      :style="{
        '--pin-active-color': hasAlarm
          ? 'rgb(220, 38, 38)'
          : isSuperviseDetected
            ? 'rgb(20, 184, 166)'
            : 'var(--device-color-binary-sensor)',
      }"
      :title="device.deviceName">
      <!-- The sensor's own figure inside the status ring (P13) — a lit contact
           fills it, the alarm/teach-in tones read through the pin's token —
           else the drawn eye. -->
      <ControlStatusRing :value="isActive || hasAlarm ? 100 : 0">
        <!-- A device picture — the state image while active, else the device's
             own thumbnail — fills the ring the way the light pin's thumbnail
             does; the drawing stays the fallback. -->
        <div v-if="pinThumbUrl && !pinThumbFailed" class="binary-icon-wrapper">
          <div class="binary-icon-thumb">
            <img
              :src="pinThumbUrl"
              :alt="device.deviceName"
              class="binary-icon-img"
              draggable="false"
              @error="pinThumbFailed = true" />
          </div>
        </div>
        <BinarySensorAnimation
          v-else-if="sensorKind !== 'generic'"
          class="pin-figure"
          :kind="sensorKind"
          :active="isActive"
          :style="pinFigureStyle" />
        <ControlIcon v-else name="eye" :size="pinIconSize" :style="pinFigureStyle" />
      </ControlStatusRing>
      <div
        v-if="isActive || hasAlarm"
        class="device-icon-pin__glow"
        :style="
          hasAlarm
            ? { background: 'rgba(220,38,38,0.4)' }
            : isSuperviseDetected
              ? { background: 'rgba(20,184,166,0.4)' }
              : {}
        "></div>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face (the grid the pages draw): the sensor's own drawing, its name
         and its state; the 24 h history is the detail the largest size has room
         for. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="isActive || hasAlarm"
      :accent="cardAccent"
      class="binary-card-face"
      :class="[`binary-card-face--${heroTone}`, { 'binary-card-face--door': sensorKind === 'door' }]"
      @click="emit('open-control-modal')"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <!-- Active: the state image (a photo of the actual window open) outranks
             the idle thumbnail, which stays the face while the sensor is clear.
             Raw <img> rather than DeviceThumbnail: that component is a fixed 2rem
             circle for the compact tile, which left the card's photo tiny.
             A clear sensor's photo is drawn unlit (grey); it takes its colour
             back the moment the sensor reads. -->
        <img
          v-if="activeThumbUrl"
          :src="activeThumbUrl"
          alt=""
          class="binary-card-face__img"
          :class="{ 'binary-card-face__img--clear': !isImageLit }"
          draggable="false" />
        <img
          v-else-if="defaultThumbUrl"
          :src="defaultThumbUrl"
          alt=""
          class="binary-card-face__img"
          :class="{ 'binary-card-face__img--clear': !isImageLit }"
          draggable="false" />
        <BinarySensorAnimation
          v-else-if="sensorKind !== 'generic'"
          :kind="sensorKind"
          :active="isActive"
          class="binary-card-face__figure" />
        <span v-else class="material-symbols-outlined binary-card-face__glyph">{{ sensorIcon }}</span>
      </template>

      <!-- The status word (Open / Closed, Detected / Clear) rides every size: on
           a sensor card it is the reading, and min's picture alone would not say
           whether the window is open. -->
      <template #value>
        <span class="binary-card-face__value" :class="{ 'binary-card-face__value--alarm': hasAlarm }">{{
          stateLabel
        }}</span>
      </template>

      <template v-if="cardSize === 'max'" #control>
        <div class="binary-sensor-history binary-card-face__history" :title="historyBarTitle">
          <div class="binary-sensor-history-bar">
            <div
              v-for="(segment, idx) in displayedHistorySegments"
              :key="`${segment.startTime}-${idx}`"
              class="binary-sensor-history-bar__segment"
              :class="{ 'binary-sensor-history-bar__segment--nodata': segment.noData }"
              :style="{
                width: `${segment.widthPct}%`,
                backgroundColor: segment.color,
                opacity: segment.noData ? 0.4 : 1,
              }"></div>
          </div>
        </div>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="sensorIcon"
      :name="device.deviceName"
      :is-on="isActive"
      active-color="amber-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <div class="binary-sheet" :class="{ 'alarm-control-flash': hasAlarm }">
      <!-- Hero: the device's own picture (the state image while active) at sheet
           size, else the sensor's own drawing in its state's colour. -->
      <div class="binary-hero" :class="`binary-hero--${heroTone}`">
        <img v-if="pinThumbUrl" :src="pinThumbUrl" alt="" class="binary-hero__img" draggable="false" />
        <ControlIcon v-else-if="heroKind === 'generic'" name="activity" size="3.25rem" />
        <BinarySensorAnimation v-else :kind="heroKind" :active="isActive" />
      </div>

      <!-- The state row: what it reads, and what kind of sensor it is -->
      <div class="binary-state" :class="`binary-state--${heroTone}`">
        <div class="binary-state-word">{{ hasAlarm ? 'ALARM' : isActive ? activeLabel : inactiveLabel }}</div>
        <div class="binary-state-type">{{ sensorTypeLabel }}</div>
      </div>

      <p v-if="lastUpdated" class="binary-updated">
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>

      <!-- Activity timeline -->
      <SetpointTimeline
        :device-values="device.deviceValues ?? []"
        :device-id="device.id"
        accent-color="rgb(59, 130, 246)"
        inactive-color="transparent"
        :transparent-track="true" />

      <!-- Alarm automation controls -->
      <div v-if="hasBinarySensorAlarm" class="binary-alarm">
        <div class="binary-section-label">Alarm</div>

        <!-- Alarm state: an incident, so it gets the loudest row -->
        <div v-if="hasAlarm" class="binary-alarm-banner">
          <span class="binary-alarm-banner-icon"><ControlIcon name="bell" size="1.15em" /></span>
          <span class="binary-alarm-banner-text">{{ tt('Alarm Triggered', 'Alarm sprožen') }}</span>
          <ControlChip :accent="ALARM_RED" @click="clearAlarm">{{ tt('Clear', 'Počisti') }}</ControlChip>
        </div>

        <!-- 3-way mode selector: Disarmed / Supervise / Armed -->
        <div class="binary-alarm-modes">
          <ControlChip
            :accent="MODE_ACCENTS.disarmed"
            :active="!isArmed && !isSupervise"
            @click="setAlarmMode('disarmed')">
            <ControlIcon name="shield-off" size="1.05em" />
            {{ tt('Disarmed', 'Deaktiviran') }}
          </ControlChip>
          <ControlChip :accent="MODE_ACCENTS.supervise" :active="isSupervise" @click="setAlarmMode('supervise')">
            <ControlIcon name="eye" size="1.05em" />
            {{ tt('Supervise', 'Nadzor') }}
          </ControlChip>
          <ControlChip :accent="MODE_ACCENTS.armed" :active="isArmed" @click="setAlarmMode('armed')">
            <ControlIcon name="shield" size="1.05em" />
            {{ tt('Armed', 'Aktiviran') }}
          </ControlChip>
        </div>

        <!-- Alarm value status (always visible) -->
        <div class="binary-alarm-status">
          <span class="binary-alarm-status-label">
            <ControlIcon name="bell" size="1.05em" :class="{ 'is-triggered': hasAlarm }" />
            {{ tt('Alarm Status', 'Stanje alarma') }}
          </span>
          <span class="binary-alarm-status-value" :class="{ 'is-triggered': hasAlarm }">
            {{ hasAlarm ? tt('Triggered', 'Sprožen') : 'OK' }}
          </span>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import DeviceCardShell from './DeviceCardShell.vue';
import { binarySensorKindOf } from './deviceWidgetTypes';
import BinarySensorAnimation from './binary-sensor/BinarySensorAnimation.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { getStateThumbnailUrl, getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import SetpointTimeline from './SetpointTimeline.vue';
import { apiFetch } from '../../services/api';
import { useWebRTCStore } from '../../stores/webrtc';
import { BINARY_SENSOR_STATE_KEYS, findBinarySensorValue, isBinarySensorValueActive } from '@/utils/binarySensorState';
import { tt } from '../../utils/consoleText';
import type { Bilingual } from '../../config/bilingual';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });
const webrtcStore = useWebRTCStore();

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const historySegments = ref<Array<{ startTime: string; widthPct: number; color: string; noData?: boolean }>>([]);
const HISTORY_WINDOW_MS = 24 * 60 * 60 * 1000;
const PRIMARY_HISTORY_KEYS = [
  'sensor_value',
  'occupancy',
  'state',
  'power',
  'on_off',
  'contact',
  'motion',
  'presence',
  'detected',
  'open',
  'smoke',
  'water',
  'leak',
  'tamper',
  'vibration',
];
const SENSOR_SUBTYPES = [
  'generic',
  'door',
  'window',
  'contact',
  'motion',
  'proximity',
  'smoke',
  'leak',
  'vibration',
  'tamper',
] as const;
type SensorSubtype = (typeof SENSOR_SUBTYPES)[number];
let historyRequestToken = 0;

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

function isKnownSensorSubtype(value: unknown): value is SensorSubtype {
  return typeof value === 'string' && SENSOR_SUBTYPES.includes(value as SensorSubtype);
}

function deriveSensorSubtypeFromDevice(
  name: string | null | undefined,
  icon: string | null | undefined,
): SensorSubtype {
  const haystack = `${name ?? ''} ${icon ?? ''}`.toLowerCase();

  if (haystack.includes('door')) return 'door';
  if (haystack.includes('window')) return 'window';
  if (haystack.includes('contact')) return 'contact';
  if (haystack.includes('proximity') || haystack.includes('near')) return 'proximity';
  if (haystack.includes('motion') || haystack.includes('occupancy') || haystack.includes('presence')) return 'motion';
  if (haystack.includes('smoke') || haystack.includes('fire')) return 'smoke';
  if (haystack.includes('water') || haystack.includes('leak') || haystack.includes('moisture')) return 'leak';
  if (haystack.includes('tamper') || haystack.includes('intrusion')) return 'tamper';
  if (haystack.includes('vibration') || haystack.includes('shock')) return 'vibration';
  return 'generic';
}

function resolveCurrentStateValue(): unknown {
  for (const key of BINARY_SENSOR_STATE_KEYS) {
    if (key in currentValues.value) {
      return currentValues.value[key];
    }
  }

  return findBinarySensorValue(props.device.deviceValues);
}

const isActive = computed(() => isBinarySensorValueActive(resolveCurrentStateValue()));

/**
 * The picture shown while the sensor reads active (window open, door ajar): the
 * device's state image when it has one. Idle keeps the default thumbnail, so the
 * list swaps between the two real photos instead of the drawn SVG.
 */
const activeThumbUrl = computed(() => (isActive.value ? getStateThumbnailUrl(props.device) : null));

/** The idle face: the device's own thumbnail, cloud-safe. */
const defaultThumbUrl = computed(() => getDefaultThumbnailUrl(props.device));

const sensorSubtype = computed<SensorSubtype>(() => {
  const explicitSubtype =
    props.device.settings && typeof props.device.settings === 'object'
      ? (props.device.settings as Record<string, unknown>).binary_sensor_subtype
      : undefined;

  if (isKnownSensorSubtype(explicitSubtype)) {
    return explicitSubtype;
  }

  return deriveSensorSubtypeFromDevice(props.device.deviceName, props.device.icon);
});

const SENSOR_ICONS: Record<SensorSubtype, string> = {
  door: 'sensor_door',
  window: 'window',
  motion: 'motion_sensor_active',
  proximity: 'sensor_occupied',
  contact: 'sensor_door',
  smoke: 'detector_smoke',
  leak: 'water_damage',
  vibration: 'vibration',
  tamper: 'shield',
  generic: 'circle_notifications',
};

const SENSOR_LABELS: Record<SensorSubtype, { active: Bilingual; inactive: Bilingual; type: Bilingual }> = {
  door: {
    active: { en: 'Open', sl: 'Odprto' },
    inactive: { en: 'Closed', sl: 'Zaprto' },
    type: { en: 'Door Sensor', sl: 'Vratni senzor' },
  },
  window: {
    active: { en: 'Open', sl: 'Odprto' },
    inactive: { en: 'Closed', sl: 'Zaprto' },
    type: { en: 'Window Sensor', sl: 'Okenski senzor' },
  },
  motion: {
    active: { en: 'Detected', sl: 'Zaznano gibanje' },
    inactive: { en: 'Clear', sl: 'Ni gibanja' },
    type: { en: 'Motion Sensor', sl: 'Senzor gibanja' },
  },
  proximity: {
    active: { en: 'Near', sl: 'Blizu' },
    inactive: { en: 'Clear', sl: 'Ni bližine' },
    type: { en: 'Proximity Sensor', sl: 'Senzor bližine' },
  },
  contact: {
    active: { en: 'Open', sl: 'Odprto' },
    inactive: { en: 'Closed', sl: 'Zaprto' },
    type: { en: 'Contact Sensor', sl: 'Kontaktni senzor' },
  },
  smoke: {
    active: { en: 'Detected!', sl: 'Zaznan dim!' },
    inactive: { en: 'Clear', sl: 'Ni dima' },
    type: { en: 'Smoke Detector', sl: 'Detektor dima' },
  },
  leak: {
    active: { en: 'Leak', sl: 'Uhajanje' },
    inactive: { en: 'Dry', sl: 'Suho' },
    type: { en: 'Leak Sensor', sl: 'Senzor uhajanja' },
  },
  vibration: {
    active: { en: 'Detected', sl: 'Zaznano tresenje' },
    inactive: { en: 'Still', sl: 'Miruje' },
    type: { en: 'Vibration Sensor', sl: 'Senzor tresenja' },
  },
  tamper: {
    active: { en: 'Alert', sl: 'Opozorilo' },
    inactive: { en: 'Secure', sl: 'Varno' },
    type: { en: 'Tamper Sensor', sl: 'Senzor posega' },
  },
  generic: {
    active: { en: 'Active', sl: 'Aktivno' },
    inactive: { en: 'Inactive', sl: 'Neaktivno' },
    type: { en: 'Binary Sensor', sl: 'Binarni senzor' },
  },
};

const sensorIcon = computed(() => props.device.icon || SENSOR_ICONS[sensorSubtype.value] || 'circle_notifications');

/** The alarm's own red — the widget's alarm colour, spelled once for the sheet. */
const ALARM_RED = 'rgb(220, 38, 38)';
/** Each alarm mode keeps the colour the old segmented control gave it. */
const MODE_ACCENTS = {
  disarmed: 'rgb(var(--gray-400))',
  supervise: 'rgb(20, 184, 166)',
  armed: 'rgb(34, 197, 94)',
} as const;

/**
 * The hero's drawing — resolved the way the tile's is (the subtype setting, then
 * the icon the sensor was saved with and the name; see `binarySensorKindOf`), so
 * the sheet draws the same figure the tile does.
 */
const heroKind = computed(() =>
  binarySensorKindOf({
    icon: props.device.icon,
    deviceName: props.device.deviceName,
    shortName: props.device.shortName,
    deviceType: props.device.deviceType,
    controlType: props.device.deviceControl || props.device.controlType,
    settings: props.device.settings,
  }),
);

/** The hero's and the state row's tone: the alarm outranks a detection, which outranks clear. */
const heroTone = computed(() =>
  hasAlarm.value ? 'alarm' : isSuperviseDetected.value ? 'supervise' : isActive.value ? 'active' : 'idle',
);

/**
 * The card's fill tone — the same tone the surface breathes, so border, fill,
 * name and breath are one colour: a red alarm, the warning amber of a
 * detection, the supervise teal. Null on a clear sensor, so an idle card keeps
 * the neutral surface the lights and switches show when off.
 */
const cardAccent = computed<string | null>(() => (heroTone.value === 'idle' ? null : 'var(--binary-card-tone)'));

/** Whether the card's picture is drawn lit — the reading, or the alarm over it. */
const isImageLit = computed(() => isActive.value || hasAlarm.value);

/**
 * Which illustration the tile draws in place of the glyph — the subtype the
 * picker writes first, then the icon the sensor was saved with and the name
 * (see `binarySensorKindOf`), so a pick always outranks the glyph.
 */
const sensorKind = computed(() =>
  binarySensorKindOf({
    icon: props.device.icon,
    deviceName: props.device.deviceName,
    shortName: props.device.shortName,
    deviceType: props.device.deviceType,
    controlType: props.device.deviceControl || props.device.controlType,
    settings: props.device.settings,
  }),
);
/** One of the sensor's own state words, in the reader's language. */
function sensorLabel(kind: 'active' | 'inactive' | 'type'): string {
  const entry = SENSOR_LABELS[sensorSubtype.value] ?? SENSOR_LABELS.generic;
  return tt(entry[kind].en, entry[kind].sl);
}
const activeLabel = computed(() => sensorLabel('active'));
const inactiveLabel = computed(() => sensorLabel('inactive'));
const sensorTypeLabel = computed(() => sensorLabel('type'));

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

const historySourceValue = computed(() => {
  const values = props.device.deviceValues ?? [];

  return (
    values.find((v) => {
      const key = String(v?.valueKey ?? '').toLowerCase();
      return v.loggingType === 'Digital' && PRIMARY_HISTORY_KEYS.includes(key) && !isAlarmHistoryKey(key);
    }) ??
    values.find((v) => {
      const key = String(v?.valueKey ?? '').toLowerCase();
      return v.loggingType === 'Digital' && !isAlarmHistoryKey(key);
    }) ??
    values.find((v) => {
      const key = String(v?.valueKey ?? '').toLowerCase();
      return v.loggingType && v.loggingType !== 'None' && !isAlarmHistoryKey(key);
    })
  );
});

const shouldLoadButtonHistory = computed(
  () => props.mode === 'card' && !(props.displayMode === 'room' && webrtcStore.connected),
);

const displayedHistorySegments = computed(() => {
  if (historySegments.value.length) return historySegments.value;

  return [
    {
      startTime: 'current',
      widthPct: 100,
      color: isActive.value ? 'var(--binary-card-history-tone, var(--device-color-binary-sensor))' : 'transparent',
    },
  ];
});

/** The sensor's own word for what it reads right now. */
const stateLabel = computed(() => (isActive.value ? activeLabel.value : inactiveLabel.value));

const historyBarTitle = computed(() =>
  shouldLoadButtonHistory.value && historySourceValue.value
    ? `${stateLabel.value} • ${tt('last 24h', 'zadnjih 24 h')}`
    : stateLabel.value,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn fallback glyph's size inside the pin (P13), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

/** The pin figure's tone (P13) — the pin's own token while it reads, grey when clear. */
const pinFigureStyle = computed(() => (isActive.value || hasAlarm.value ? { color: 'var(--pin-active-color)' } : {}));

/** The picture inside the pin and the sheet's hero: active state first, then
    the device's own thumbnail. */
const pinThumbUrl = computed(() => activeThumbUrl.value ?? defaultThumbUrl.value);

// A different picture is a new chance: a link that failed once must not keep
// the pin empty for a thumbnail that has since resolved.
const pinThumbFailed = ref(false);
watch(pinThumbUrl, () => {
  pinThumbFailed.value = false;
});

// ── Binary Sensor Alarm state ────────────────────────────────────────────
/** Resolve alarm value key — supports both prefixed and unprefixed forms */
const alarmKey = (short: string) => {
  const prefixed = `binary_sensor_alarm__${short}`;
  if (prefixed in currentValues.value) return prefixed;
  if (short in currentValues.value) return short;
  return prefixed; // fallback
};

const hasBinarySensorAlarm = computed(() => {
  return 'binary_sensor_alarm__armed' in currentValues.value || 'armed' in currentValues.value;
});

const isArmed = computed(() => {
  const v = currentValues.value[alarmKey('armed')];
  return v === true || v === 1 || v === 'true';
});

const isSupervise = computed(() => {
  const v = currentValues.value[alarmKey('supervise')];
  return v === true || v === 1 || v === 'true';
});

const hasAlarm = computed(() => {
  const v = currentValues.value[alarmKey('alarm')];
  return v === true || v === 1 || v === 'true';
});

/** Supervise mode + sensor active → walking man indicator + teal breathing widget */
const isSuperviseDetected = computed(() => isSupervise.value && isActive.value && !hasAlarm.value);

const setAlarmMode = async (mode: 'disarmed' | 'supervise' | 'armed') => {
  try {
    if (mode === 'armed') {
      await apiFetch(`/devices/${props.device.id}/values/${alarmKey('armed')}/control`, {
        method: 'POST',
        body: JSON.stringify({ value: true }),
      });
    } else if (mode === 'supervise') {
      await apiFetch(`/devices/${props.device.id}/values/${alarmKey('supervise')}/control`, {
        method: 'POST',
        body: JSON.stringify({ value: true }),
      });
    } else {
      // disarmed: turn off armed (backend clears supervise too)
      await apiFetch(`/devices/${props.device.id}/values/${alarmKey('armed')}/control`, {
        method: 'POST',
        body: JSON.stringify({ value: false }),
      });
    }
  } catch (err) {
    console.error('Failed to set alarm mode', err);
  }
};

const clearAlarm = async () => {
  try {
    const key = alarmKey('alarm');
    await apiFetch(`/devices/${props.device.id}/values/${key}/control`, {
      method: 'POST',
      body: JSON.stringify({ value: false }),
    });
  } catch (err) {
    console.error('Failed to clear alarm', err);
  }
};

function isAlarmHistoryKey(key: string) {
  return key.startsWith('binary_sensor_alarm__') || ['alarm', 'armed', 'supervise', 'pending_trigger_at'].includes(key);
}

function isTruthyHistoryValue(value: unknown) {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'on';
}

function historySegmentColor(value: unknown) {
  // The card's own state tone while it reads, the sensor's purple on a clear
  // card (and in the compact tile and the sheet, where no tone is set).
  return isTruthyHistoryValue(value)
    ? 'var(--binary-card-history-tone, var(--device-color-binary-sensor))'
    : 'transparent';
}

function buildHistorySegments(dataPoints: Array<{ timestamp: string; value: unknown }>, start: Date, end: Date) {
  const totalMs = Math.max(1, end.getTime() - start.getTime());
  const points = [...dataPoints]
    .filter((point) => !!point?.timestamp)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  if (!points.length) return [];

  const segments: Array<{ startTime: string; widthPct: number; color: string; noData?: boolean }> = [];

  const pushSegment = (segmentStart: Date, segmentEnd: Date, value: unknown, noData = false) => {
    const ms = segmentEnd.getTime() - segmentStart.getTime();
    if (ms <= 0) return;

    segments.push({
      startTime: segmentStart.toISOString(),
      widthPct: (ms / totalMs) * 100,
      color: noData ? 'rgba(148, 163, 184, 0.28)' : historySegmentColor(value),
      noData,
    });
  };

  const firstTimestamp = new Date(points[0].timestamp);
  if (firstTimestamp > start) pushSegment(start, firstTimestamp, null, true);

  for (let i = 0; i < points.length; i++) {
    const segmentStart = new Date(points[i].timestamp);
    const nextTimestamp = i < points.length - 1 ? new Date(points[i + 1].timestamp) : end;
    pushSegment(
      segmentStart < start ? start : segmentStart,
      nextTimestamp > end ? end : nextTimestamp,
      points[i].value,
    );
  }

  return segments;
}

async function loadButtonHistory() {
  if (!shouldLoadButtonHistory.value) {
    historySegments.value = [];
    return;
  }

  const sourceValue = historySourceValue.value;
  if (!sourceValue?.id) {
    historySegments.value = [];
    return;
  }

  const requestToken = ++historyRequestToken;
  const end = new Date();
  const start = new Date(end.getTime() - HISTORY_WINDOW_MS);

  try {
    const response = await apiFetch(
      `/values/${sourceValue.id}/history?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`,
    );

    if (requestToken !== historyRequestToken) return;
    historySegments.value = buildHistorySegments(response?.data ?? [], start, end);
  } catch {
    if (requestToken !== historyRequestToken) return;
    historySegments.value = [];
  }
}

watch(
  [
    () => historySourceValue.value?.id,
    isActive,
    () => props.mode,
    () => props.displayMode,
    () => webrtcStore.connected,
  ],
  ([, , mode]) => {
    if (mode === 'card') {
      void loadButtonHistory();
    } else {
      historySegments.value = [];
    }
  },
  { immediate: true },
);
</script>

<style scoped>
/* ── Icon mode: the sensor's own figure inside the pin's ring (P13) ──────
   A percentage rather than a px step, so the same drawing reads at `--sm`,
   at `--lg` and in a floor-plan pin (which sizes itself from its container). */
.device-icon-pin .pin-figure {
  width: 60%;
  height: 60%;
}

/* A device picture fills the ring the way the light pin's thumbnail does. */
.binary-icon-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.binary-icon-thumb {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
}

.binary-icon-img {
  width: 100%;
  height: 100%;
  /* A window/door icon stays whole inside its ring (owner, 25.10.2026). */
  object-fit: contain;
  object-position: center;
}

/* ── Button mode: the device tiles' own head ──────────────────────────────
   The sensor's icon in a 2.35em circle, then its name over the state it reads;
   the 24 h history sits at the bottom the way a light tile carries its bar. */
.binary-sensor-layout {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  margin-top: 0;
}
.device-button-head {
  display: flex;
  align-items: center;
  gap: 0.55em;
  min-width: 0;
}
.device-button-text {
  display: flex;
  flex-direction: column;
  gap: 0.1em;
  min-width: 0;
}
/* The name stays small; the state word under it carries the weight */
.binary-sensor-layout .device-name {
  font-size: 0.62em;
  min-width: 0;
}
.device-current-value {
  font-size: var(--tile-status-size);
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}
.device-icon--circle {
  width: 2.35em;
  height: 2.35em;
  margin-bottom: 0;
  border-radius: 50%;
  background: var(--icon-circle-fill);
  border: 1px solid rgba(148, 163, 184, 0.35);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
/* The sensor's own photo fills the circle */
.device-icon--circle :deep(.device-thumb) {
  width: 100%;
  height: 100%;
}

/* ── State colour ─────────────────────────────────────────────────────────
   One `color` per state (detected / alarm / supervise-detected) drives the
   border, the circle's tint and glow, the name, the state word and the pulse,
   so light and dark stay in sync — the app theme is the `.dark` class, not the
   OS preference the old rules keyed on. */
:is(.active, .alarm-active, .supervise-detected) {
  border-color: color-mix(in srgb, currentColor 60%, transparent) !important;
  background-color: transparent !important;
}
:is(.active, .alarm-active, .supervise-detected) .device-icon--circle {
  background: color-mix(in srgb, currentColor 14%, var(--icon-circle-fill));
  border-color: color-mix(in srgb, currentColor 45%, transparent);
  box-shadow:
    0 0 10px color-mix(in srgb, currentColor 28%, transparent),
    0 0 24px color-mix(in srgb, currentColor 12%, transparent);
}
/* The layered utilities paint the name and the glyph, so each needs the tile's
   own colour spelled out (the room tiles' own note). */
:is(.active, .alarm-active, .supervise-detected) .device-name,
:is(.active, .alarm-active, .supervise-detected) .device-current-value,
:is(.active, .alarm-active, .supervise-detected) .device-icon--circle .material-symbols-outlined {
  color: currentColor !important;
}

/* Detected: the sensor's own colour, and the tile's edge breathes (the pulse
   itself is the overlay below). */
.active {
  color: var(--device-color-binary-sensor);
}

/* Alarm: red, pulsing */
.alarm-active {
  color: rgb(220 38 38);
}
.dark .alarm-active {
  color: rgb(248 113 113);
}

/* Supervise + sensor active: teal (mirrors the alarm; the walking-man badge
   rides in the shared automation overlay) */
.supervise-detected {
  color: rgb(20 184 166);
}
.dark .supervise-detected {
  color: rgb(45 212 191);
}

/* The state pulse is an OVERLAY whose opacity animates. Animating the tile's
   border-color / box-shadow (what these keyframes used to do) repaints every
   active tile on every frame; opacity is composited, so a wall of detected
   sensors costs nothing per frame. One rule for all three states — the tone is
   the tile's own `color` — and the cadence each state had. */
.device-button:is(.active, .alarm-active, .supervise-detected)::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 1.5px solid currentColor;
  pointer-events: none;
  animation: tile-state-pulse 2.6s ease-in-out infinite;
}
.device-button.alarm-active::after {
  animation-duration: 1.2s;
}
.device-button.supervise-detected::after {
  animation-duration: 1.6s;
}

@keyframes tile-state-pulse {
  0%,
  100% {
    opacity: 0.16;
  }
  50% {
    opacity: 0.8;
  }
}

/* Alarm zone indicator row (inside the shared .automation-overlay) */
.binary-sensor-widget__alarm-indicator-wrap {
  display: flex;
  align-items: center;
  gap: 3px;
}

.alarm-indicator-label {
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
}

.binary-sensor-history {
  width: 100%;
  margin-top: 0.25em;
}

.binary-sensor-history-bar {
  display: flex;
  width: 100%;
  height: 0.82em;
  border-radius: 4px;
  overflow: hidden;
  background: transparent;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.binary-sensor-history-bar__segment {
  min-width: 2px;
  height: 100%;
  flex: 0 0 auto;
}

.binary-sensor-history-bar__segment--nodata {
  background: rgba(148, 163, 184, 0.28) !important;
}

.dark .binary-sensor-history-bar {
  background: transparent;
  border-color: rgba(107, 114, 128, 0.28);
}
.dark .binary-sensor-history-bar__segment--nodata {
  background: rgba(75, 85, 99, 0.5) !important;
}

@media (prefers-reduced-motion: reduce) {
  .animate-ping {
    animation: none;
  }
}

/* Control mode alarm flash. The wash colour rides a variable so the dark copy
   is the `.dark` class like every other rule in this widget. */
.alarm-control-flash {
  --alarm-flash: rgba(220, 38, 38, 0.08);
  animation: alarm-control-pulse 1.5s ease-in-out infinite;
  border-radius: 0.5rem;
}
.dark .alarm-control-flash {
  --alarm-flash: rgba(248, 113, 113, 0.08);
}
@keyframes alarm-control-pulse {
  0%,
  100% {
    background-color: transparent;
  }
  50% {
    background-color: var(--alarm-flash);
  }
}

/* ── Control mode: the sheet body ────────────────────────────────────────
   The sensor's own drawing as the hero, then the state row, then the rows
   (the timeline and the alarm controls). The tone classes are the tile's own
   colours — the alarm's red, the supervise teal, the sensor's purple — so the
   sheet and the tile read the same state the same way. */
.binary-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.875rem * var(--ui-space-factor));
}

.binary-hero {
  width: 6.5rem;
  height: 6.5rem;
  margin: 0 auto;
  color: rgb(var(--gray-400));
}

.binary-hero__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.dark .binary-hero {
  color: rgb(var(--gray-500));
}

.binary-hero--active {
  color: var(--device-color-binary-sensor);
}

.binary-hero--supervise {
  color: rgb(20, 184, 166);
}

.dark .binary-hero--supervise {
  color: rgb(45, 212, 191);
}

/* The one tone that moves: an uncleared alarm breathes. */
.binary-hero--alarm {
  color: rgb(220, 38, 38);
  animation: binary-hero-alarm 1.5s ease-in-out infinite;
}

.dark .binary-hero--alarm {
  color: rgb(248, 113, 113);
}

@keyframes binary-hero-alarm {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.binary-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  text-align: center;
}

.binary-state-word {
  font-size: 1.125rem;
  font-weight: 700;
}

.binary-state--active .binary-state-word {
  color: var(--device-color-binary-sensor);
}

.binary-state--supervise .binary-state-word {
  color: rgb(20, 184, 166);
}

.dark .binary-state--supervise .binary-state-word {
  color: rgb(45, 212, 191);
}

.binary-state--alarm .binary-state-word {
  color: rgb(220, 38, 38);
}

.dark .binary-state--alarm .binary-state-word {
  color: rgb(248, 113, 113);
}

.binary-state--idle .binary-state-word {
  color: rgb(var(--gray-500));
}

.dark .binary-state--idle .binary-state-word {
  color: rgb(var(--gray-400));
}

.binary-state-type {
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .binary-state-type {
  color: rgb(var(--gray-400));
}

.binary-updated {
  margin: 0;
  text-align: center;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .binary-updated {
  color: rgb(var(--gray-400));
}

.binary-alarm {
  display: flex;
  flex-direction: column;
  gap: calc(0.75rem * var(--ui-space-factor));
  padding-top: calc(0.875rem * var(--ui-space-factor));
  border-top: 1px solid var(--glass-edge-soft);
}

.binary-section-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .binary-section-label {
  color: rgb(var(--gray-400));
}

.binary-alarm-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: calc(0.625rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid rgba(220, 38, 38, 0.35);
  border-radius: calc(0.75rem * var(--ui-radius-factor));
  background: rgba(220, 38, 38, 0.08);
  color: rgb(220, 38, 38);
}

.dark .binary-alarm-banner {
  border-color: rgba(248, 113, 113, 0.35);
  background: rgba(248, 113, 113, 0.1);
  color: rgb(248, 113, 113);
}

.binary-alarm-banner-icon {
  display: inline-flex;
  animation: binary-bell-pulse 1.5s ease-in-out infinite;
}

@keyframes binary-bell-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.binary-alarm-banner-text {
  flex: 1 1 auto;
  font-size: 0.875rem;
  font-weight: 600;
}

.binary-alarm-modes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.binary-alarm-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0 0.25rem;
}

.binary-alarm-status-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8125rem;
  color: rgb(var(--gray-700));
}

.dark .binary-alarm-status-label {
  color: rgb(var(--gray-300));
}

.binary-alarm-status-label .is-triggered {
  color: rgb(220, 38, 38);
}

.binary-alarm-status-value {
  font-size: 0.8125rem;
  font-weight: 700;
  color: rgb(22, 163, 74);
}

.dark .binary-alarm-status-value {
  color: rgb(74, 222, 128);
}

.binary-alarm-status-value.is-triggered {
  color: rgb(220, 38, 38);
}

.dark .binary-alarm-status-value.is-triggered {
  color: rgb(248, 113, 113);
}

@media (prefers-reduced-motion: reduce) {
  .device-button:is(.active, .alarm-active, .supervise-detected)::after {
    animation: none;
    /* A steady rim instead of a pulse. */
    opacity: 0.6;
  }
  .binary-hero--alarm,
  .alarm-control-flash,
  .binary-alarm-banner-icon {
    animation: none;
  }
}

/* ── Card face ───────────────────────────────────────────────────────────────
   The shell owns the frame, the name and the size rules; these are the device's
   own parts inside it. */
.binary-card-face__img,
.binary-card-face__figure {
  width: 100%;
  height: 100%;
}

.binary-card-face__img {
  object-fit: contain;
  /* The photo sits on the card's own surface, so its corners follow the same
     roundness preset the controls do. */
  border-radius: var(--radius-lg, 0.5rem);
  transition:
    filter 0.2s ease,
    opacity 0.2s ease;
}

/* Clear: the picture is there, just unlit — the reading is what lights it (the
   switch card's own idle treatment, kept lighter so a wall of clear sensors
   still reads as a photo). */
.binary-card-face__img--clear {
  filter: grayscale(1);
  opacity: 0.72;
}

.binary-card-face__figure :deep(svg),
.binary-card-face__figure svg {
  width: 100%;
  height: 100%;
}

/* Same weight as the light card's glyph fallback (`light-card-face__glyph`). */
.binary-card-face__glyph {
  font-size: 2.4rem;
}

.binary-card-face__history {
  width: 100%;
}

.binary-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #64748b;
}

/* Min keeps the status but takes its space back from the type: the smallest
   card is the picture, the name and the one word that matters. */
.device-card--min .binary-card-face__value {
  font-size: 0.72rem;
  line-height: 1.15;
}

.dark .binary-card-face__value {
  color: #94a3b8;
}

.binary-card-face__value--alarm {
  color: #b91c1c;
}

.dark .binary-card-face__value--alarm {
  color: #fca5a5;
}

/* ── The warning surface ─────────────────────────────────────────────────────
   The card's background pulses in the state's own tone: a warning amber flash
   while the sensor reads (a window left open, a body in the room), a quicker
   red when the alarm has fired, teal for a supervised detection. The wash is
   one pseudo-element whose opacity animates (composited), so a wall of reading
   sensors costs nothing per frame — the compact tile's own rule.

   The state is drawn as a nearly-transparent tint plus a glowing edge, NOT the
   shell's accent fill: at its 26 % mix a reading sensor sitting beside a
   coloured lamp read as another lamp (owner, 25.10.2026: *"background on binary
   sensor dont work well. can be mixed with color lamps.. can we try more
   transparent like colors and add more glow to borders"*).

   The pulse is a flash, not a slow breath (owner, 25.10.2026: *"the breathing
   should be more like a flashing warning effect. bit faster. more visible
   transitions"*): the keyframes rise in the first third and fall slowly, so the
   eye catches the on-beat, and the floor is low enough that the off-beat
   reads as off. */
.binary-card-face {
  --binary-card-tone: rgb(220, 38, 38);
  --binary-card-tone-low: 0.04;
  --binary-card-tone-high: 0.3;
  --binary-card-halo-low: 0.3;
  --binary-card-breath: 0.9s;
  /* The history bar keeps the sensor's own purple while the card is clear and
     takes the state's tone while it reads (the `--reading` rule below). */
  --binary-card-history-tone: var(--device-color-binary-sensor);
  position: relative;
  isolation: isolate;
}

/* The shell's own soft edge and faint wash are exactly this card's surface; only
   its static glow is silenced, because the flashing halo below is the signal. */
.binary-card-face.device-card--accent.device-card--on {
  box-shadow: none;
}

/* Detected: the warning tone, flashing at 1.7 s — a light orange, so it reads
   as attention rather than the alarm's red. */
.binary-card-face--active {
  --binary-card-tone: rgb(251, 146, 60);
  --binary-card-tone-low: 0.02;
  --binary-card-tone-high: 0.28;
  --binary-card-halo-low: 0.15;
  --binary-card-breath: 1.7s;
}

/* A door standing open is not a motion flash: it keeps its own light blue, so a
   door reads apart from the sensors around it (owner, 25.10.2026: *"door should
   have different color.. light blue"*). The alarm and supervise tones still
   outrank it — those are about the ZONE, not the sensor kind. */
.binary-card-face--door.binary-card-face--active {
  --binary-card-tone: rgb(96, 165, 250);
  --binary-card-tone-high: 0.22;
}

/* Supervised detection: the same flash in the supervise teal, a touch calmer. */
.binary-card-face--supervise {
  --binary-card-tone: rgb(20, 184, 166);
  --binary-card-tone-low: 0.02;
  --binary-card-tone-high: 0.24;
  --binary-card-halo-low: 0.2;
  --binary-card-breath: 2.1s;
}

.dark .binary-card-face {
  --binary-card-tone: rgb(248, 113, 113);
  --binary-card-tone-low: 0.05;
  --binary-card-tone-high: 0.34;
}

/* Dark keeps the same light orange: it is the hue that says "attention", not
   the alarm's red. */
.dark .binary-card-face--active {
  --binary-card-tone: rgb(251, 146, 60);
  --binary-card-tone-low: 0.03;
  --binary-card-tone-high: 0.3;
}

.dark .binary-card-face--door.binary-card-face--active {
  --binary-card-tone: rgb(147, 197, 253);
  --binary-card-tone-high: 0.24;
}

.dark .binary-card-face--supervise {
  --binary-card-tone: rgb(45, 212, 191);
  --binary-card-tone-low: 0.03;
  --binary-card-tone-high: 0.26;
}

.binary-card-face::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1; /* above the card's own fill, below its content */
  border-radius: inherit;
  background: var(--binary-card-tone);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

/* The halo: the state's tone laid outside the card's edge — a crisp 1 px ring
   and two blurred layers. Only its opacity animates, so the glow is composited
   like the wash and a wall of reading sensors still costs nothing per frame. */
.binary-card-face::after {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--binary-card-tone) 60%, transparent),
    0 0 0.75rem color-mix(in srgb, var(--binary-card-tone) 70%, transparent),
    0 0 1.7rem color-mix(in srgb, var(--binary-card-tone) 42%, transparent);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.binary-card-face:is(.binary-card-face--alarm, .binary-card-face--active, .binary-card-face--supervise) {
  --binary-card-history-tone: var(--binary-card-tone);
}

.binary-card-face:is(.binary-card-face--alarm, .binary-card-face--active, .binary-card-face--supervise)::before {
  animation: binary-card-warn-flash var(--binary-card-breath) ease-in-out infinite;
}

.binary-card-face:is(.binary-card-face--alarm, .binary-card-face--active, .binary-card-face--supervise)::after {
  animation: binary-card-halo-flash var(--binary-card-breath) ease-in-out infinite;
}

/* The flash: a fast rise, a slower fall (the on-beat lands at 35 % of the
   cycle), so the card reads as pulsing a warning rather than breathing. */
@keyframes binary-card-warn-flash {
  0%,
  100% {
    opacity: var(--binary-card-tone-low);
  }
  35% {
    opacity: var(--binary-card-tone-high);
  }
}

/* The halo flashes in step and never goes fully dark, so the edge stays
   readable while the tint swings. */
@keyframes binary-card-halo-flash {
  0%,
  100% {
    opacity: var(--binary-card-halo-low);
  }
  35% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .binary-card-face:is(.binary-card-face--alarm, .binary-card-face--active, .binary-card-face--supervise)::before {
    animation: none;
    /* A steady tint instead of a flash. */
    opacity: calc((var(--binary-card-tone-low) + var(--binary-card-tone-high)) / 2);
  }

  .binary-card-face:is(.binary-card-face--alarm, .binary-card-face--active, .binary-card-face--supervise)::after {
    animation: none;
    /* A steady ring instead of a flashing one. */
    opacity: 0.85;
  }
}
</style>
